import React, { useState, useEffect } from "react";
import { McTabBar, McTab } from "@maersk-global/mds-react-wrapper/components-core";
import SectionContent from "./SectionContent";
import ImageGalleryModal from "./ImageGalleryModal";
import { ChainedFiveWhysService } from "../../services/chainedFiveWhysService";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "react-query";

const hasRealText = (html) => {
  if (!html) return false;
  return html.replace(/<[^>]*>/g, "").trim().length > 0;
};

// Shared WHY row — used by all render paths
function WhyRow({ label, question, answer, isLast }) {
  return (
    <div style={{
      display: "flex",
      gap: "12px",
      padding: "10px 0",
      borderBottom: isLast ? "none" : "1px solid var(--mds_brand_appearance_default_border-color, #e8e8e8)",
    }}>
      <span style={{
        flexShrink: 0,
        fontSize: "12px",
        fontWeight: "700",
        color: "var(--mds_brand_appearance_primary_default_text-color, #0073ab)",
        letterSpacing: "0.05em",
        paddingTop: "2px",
        minWidth: "40px",
      }}>
        {label}
      </span>
      <div style={{ flex: 1 }}>
        {question && (
          <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--mds_brand_appearance_default_default_text-color, #141414)", marginBottom: "4px" }}>
            {question}
          </div>
        )}
        {answer ? (
          <div
            style={{ fontSize: "14px", color: "var(--mds_brand_appearance_default_default_text-color, #141414)", lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: answer }}
          />
        ) : (
          <span style={{ fontSize: "14px", color: "var(--mds_brand_appearance_default_default_text-color, #141414)", fontStyle: "italic" }}>
            No answer provided
          </span>
        )}
      </div>
    </div>
  );
}

function RootCauseIdentificationSection({ data, images, requiredForModal, incidentID }) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [chainedData, setChainedData] = useState([]);
  const [loadingChained, setLoadingChained] = useState(false);
  const { instance, accounts } = useMsal();
  const accountId = accounts[0]?.homeAccountId;
  const queryClient = useQueryClient();
  const chainedService = ChainedFiveWhysService();

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    setGalleryOpen(true);
  };

  useEffect(() => {
    const fetchChainedData = async () => {
      if (!incidentID || !instance || !accountId) return;
      setLoadingChained(true);
      try {
        const chains = await chainedService.getChainedFiveWhysCached(queryClient, incidentID, instance, accounts);
        setChainedData(chains && chains.length > 0 ? chains : []);
      } catch {
        setChainedData([]);
      } finally {
        setLoadingChained(false);
      }
    };
    fetchChainedData();
  }, [incidentID, instance, accountId]);

  // Normalize into a unified chains array regardless of source
  const classicQuestions = data?.questionarie?.filter((q) => q?.question || q?.answer) || [];

  let chains = [];
  if (chainedData.length > 0) {
    // Chained format — map whys to shared shape
    chains = chainedData.map((chain, i) => ({
      key: chain.chainId || i,
      letter: chain.chainLetter || String.fromCharCode(65 + i),
      label: chain.chainLabel || null,
      description: chain.chainDescription || null,
      whys: (chain.whys || []).map((w, wi) => ({
        label: `WHY ${w.whyNumber || wi + 1}`,
        question: w.question || null,
        answer: w.answer || null,
      })),
    }));
  } else if (classicQuestions.length > 0) {
    // Classic format — single synthetic chain
    chains = [{
      key: "classic",
      letter: null,
      label: null,
      description: null,
      whys: classicQuestions.map((q, i) => ({
        label: `WHY ${i + 1}`,
        question: q.question || null,
        answer: q.answer ? q.answer.replace(/\n/g, "<br/>") : null,
      })),
    }];
  }

  const multiChain = chains.length > 1;

  return (
    <div id="root-cause-identification">
      <SectionContent
        sectionData={data?.description}
        images={[]}
        requiredForModal={requiredForModal}
      />

      <div className="report-subsection">
        <p className="report-subsection-header">5 Whys Analysis</p>

        {loadingChained ? (
          <div className="report-content-box" style={{ textAlign: "center", padding: "20px", color: "var(--mds_brand_appearance_default_default_text-color, #141414)" }}>
            Loading…
          </div>
        ) : chains.length === 0 ? (
          <div className="report-content-box is-empty">NA</div>
        ) : multiChain ? (
          /* Multiple chains — MDS McTabBar */
          <McTabBar fit="small">
            {chains.map((chain) => [
              <McTab
                key={`tab-${chain.key}`}
                slot="tab"
                label={[chain.letter, chain.label].filter(Boolean).join(" — ")}
              />,
              <div key={`panel-${chain.key}`} slot="panel">
                {chain.description && hasRealText(chain.description) && (
                  <div
                    style={{
                      padding: "10px 12px",
                      fontSize: "14px",
                      color: "var(--mds_brand_appearance_default_default_text-color, #141414)",
                      lineHeight: 1.5,
                      borderBottom: "1px solid var(--mds_brand_appearance_default_border-color, #e8e8e8)",
                    }}
                    dangerouslySetInnerHTML={{ __html: chain.description }}
                  />
                )}
                {chain.whys.length > 0 ? (
                  <div style={{ padding: "0 12px" }}>
                    {chain.whys.map((why, wi) => (
                      <WhyRow
                        key={wi}
                        label={why.label}
                        question={why.question}
                        answer={why.answer}
                        isLast={wi === chain.whys.length - 1}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="report-content-box is-empty" style={{ margin: "10px 12px" }}>
                    No whys recorded
                  </div>
                )}
              </div>,
            ])}
          </McTabBar>
        ) : (
          /* Single chain — bordered card, no tab chrome */
          <div style={{
            border: "1px solid var(--mds_brand_appearance_default_border-color, #e8e8e8)",
            borderRadius: "var(--mds_brand_border_medium_radius, 4px)",
            overflow: "hidden",
          }}>
            {chains[0].whys.length > 0 ? (
              <div style={{ padding: "0 12px" }}>
                {chains[0].whys.map((why, wi) => (
                  <WhyRow
                    key={wi}
                    label={why.label}
                    question={why.question}
                    answer={why.answer}
                    isLast={wi === chains[0].whys.length - 1}
                  />
                ))}
              </div>
            ) : (
              <div className="report-content-box is-empty" style={{ margin: "10px 12px" }}>
                No whys recorded
              </div>
            )}
          </div>
        )}
      </div>

      {images && images.length > 0 && (
        <>
          <div className="report-images-grid">
            {images.map((image, index) => (
              <div
                key={index}
                className="report-image-thumbnail"
                onClick={() => handleImageClick(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleImageClick(index)}
              >
                <img
                  src={image.preview || image.url}
                  alt={image.name || `Image ${index + 1}`}
                />
                <div className="thumbnail-overlay">
                  <span className="thumbnail-icon">🔍</span>
                </div>
              </div>
            ))}
          </div>
          <ImageGalleryModal
            images={images}
            initialIndex={selectedImageIndex}
            isOpen={galleryOpen}
            onClose={() => setGalleryOpen(false)}
          />
        </>
      )}
    </div>
  );
}

export default RootCauseIdentificationSection;
